import { Helmet } from 'react-helmet-async';
import { PenTool, CheckCircle2, AlertTriangle, ShieldAlert, FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function FullWaiver() {
  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Full Liability Waiver | Open Water Syndicate</title>
        <meta name="description" content="Read our comprehensive Maritime Liability Release and Waiver of Rights, protecting OWS operations and clarifying physical safety thresholds." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 text-ows-teal mb-4">
            <ShieldAlert className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">Liability protocol // SYS-WAIVER-ALL</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark leading-none">
            Release of Liability
          </h1>
          <p className="mt-4 text-xs font-mono text-ows-dark/40 uppercase tracking-widest">
            Open Water Syndicate Global Maritime Clearance // Public Document
          </p>
        </div>

        {/* Hazard alert box */}
        <div className="mb-12 bg-[#FFF3CD] border border-[#FFEBAA] p-8 rounded-[2rem] space-y-3">
          <div className="flex items-center gap-2 text-[#856404] font-bold text-xs uppercase tracking-widest font-mono">
            <AlertTriangle className="h-4 w-4" /> BINDING STATEMENT OF GENERAL WAIVER
          </div>
          <p className="text-xs text-[#856404] font-serif leading-relaxed">
            This document represents a legally binding contract. By accessing our services, participating in 
            offshore expeditions, or renting/operating Open Water Syndicate equipment, you agree to forfeit substantial legal 
            rights, including the privilege to litigate claims of negligence, wrongful death, or equipment malfunction. Read 
            these sections in their entirety.
          </p>
        </div>

        {/* Long form waiver content */}
        <div className="bg-white rounded-[3rem] border border-ows-teal/5 shadow-xl p-8 sm:p-12 space-y-12">
          {/* Section 1 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 01 // LANDSCAPE OF INHERENT MARITIME HAZARDS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">1. Explicit Assumption of Risk</h2>
            <div className="space-y-4 text-sm text-ows-dark/70 font-serif leading-relaxed">
              <p>
                Participation in paddleboarding, hybrid maritime overlanding, coiled training, and open-water navigation 
                sponsored by Open Water Syndicate (OWS) involves active navigation of challenging channels, specifically 
                upon Lake Ontario, the Toronto Outer Harbour, Leslie Street Spit, inner island lagoons, and open coastal zones. 
                I explicitly acknowledge the presence of highly volatile physical hazards, including but not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2 font-sans text-xs text-ows-dark/80 leading-relaxed">
                <li>
                  <strong className="text-ows-dark">Hydrodynamic Conditions:</strong> Unexpected high winds, rapid temperature shifts, 
                  extreme cold-water thermals, hypothermia, cold shock, offshore currents, sudden squalls, underwater shoals, and debris.
                </li>
                <li>
                  <strong className="text-ows-dark">Commercial Traffic Wakes:</strong> Large water taxis, industrial container vessels, ferries, 
                  and high-speed motorboats operating within active marine corridors whose wakes and turbulent wave swells can trigger sudden capsizing.
                </li>
                <li>
                  <strong className="text-ows-dark">Biological & Human Fatigue:</strong> Multi-mile exposure causing muscular exhaustion, dehydration, 
                  disorientation, cramping, panic, or cardiac responses under physical duress.
                </li>
                <li>
                  <strong className="text-ows-dark">Hardware & Survival Redundancy:</strong> While OWS products like the EXPEDITION CX2 offer 
                  dual-chamber safety inflation cells, equipment is subject to unexpected mechanical failures, pressure variations, and structural stress.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 02 // PHYSICAL STANDARDS & HEALTH DISCOVERY</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">2. Physical Fitness and Operator Competence</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              I certify that I am in excellent cardiovascular and physical condition, possess the fundamental balance, coordination, and 
              swimming skills required to navigate deep open basins safely, and am not currently taking medications that impair motor control or 
              cognitive reaction times. I acknowledge that I must satisfy or declare any gear requirements (e.g. thermal wetsuits/drysuits 
              or personal flotation devices) as directed by the crew prior to stepping foot on any marine vessel.
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 03 // COMPLETE COVENANT OF RELEASE</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">3. Release of Liability and Covenant Not to Sue</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed space-y-4">
              I, on behalf of myself, my heirs, executors, and personal legal agents, do hereby 
              <strong> completely release, discharge, hold harmless, and absolve</strong> Open Water Syndicate, its founders, 
              guides, logistics managers, event staff, and commercial affiliates (the &quot;Released Parties&quot;) from any and all liability, 
              claims, demands, costs, or causes of action arising from personal injury, physical shock, hypothermia, property damage, 
              permanent disability, or wrongful death, whether caused by ordinary negligence of the guides, equipment wear-and-tear, or unexpected 
              meteorological events occurring during any launch sequence.
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 04 // ABSOLUTE GUIDE COMMAND MANDATE</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">4. Compliance with Safety Protocol Orders</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              I agree to comply strictly with every coiled warning and route instruction issued by OWS navigation experts, safety boats, 
              and harbor controllers. I understand that the guides maintain absolute sovereign command of the expedition routes. 
              Failing to maintain designated cohort configurations or disregarding instructions to don physical safety items constitutes 
              immediate grounds for portal termination, clearance certificate revocation, and physical rescue extraction at my sole personal expense.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 05 // JURISDICTION & SEVERABILITY</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">5. Legal Jurisdiction & Severability</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              These release covenants are governed strictly by the maritime laws of the Province of Ontario and federal Canadian maritime regulations. 
              If any clause contained in this document is declared invalid by a court of competent jurisdiction, that specific fragment shall be 
              severed without impacting the legal validity of the remaining release covenants.
            </p>
          </div>
        </div>

        {/* Dynamic routing call block to sign a waiver if they have an active booking */}
        <div className="mt-12 bg-ows-sand border border-ows-teal/10 p-8 rounded-[2rem] flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#0d3a40]">Have an Active Booking or Member Profile?</h4>
            <p className="text-[11px] text-ows-dark/70 font-serif leading-relaxed mt-1">
              If you need to sign your personalized digital waiver file, locate the unique waiver link from your confirmation email 
              or enter your secure Portal to complete digital signatures.
            </p>
          </div>
          <Link 
            to="/member-portal" 
            className="inline-flex items-center gap-2 bg-ows-teal text-white px-6 py-3 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-ows-dark transition-all whitespace-nowrap"
          >
            Enter Portal <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Contact info block */}
        <div className="mt-12 text-center">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ows-dark/40">
            For specific marine policy queries, contact command: <span className="text-ows-teal font-bold underline">contact@openwatersyndicate.com</span>
          </p>
        </div>
      </div>
    </div>
  );
}
